import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgToastTranslocoService } from 'src/app/common/services/ng-toast-transloco/ng-toast-transloco.service';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { LoginService } from '../../services/login.service';
import { LoginDetailsService } from '../../services/login-details.service';
import { ContentsService } from 'src/app/shared/services/contents.service';
import { emailPattern, encryptionKey } from 'src/app/const/constant';

@Component({
  selector: 'ds-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
})
export class ForgotPasswordComponent implements OnChanges {
  @Output() resetPasswordEvent = new EventEmitter<{
    username: string;
    maskedEmail: string;
  }>();
  @Output() goToSignin = new EventEmitter();
  @Output() showVerifyDetails = new EventEmitter<string>();
  @Output() showOtpVerification = new EventEmitter<{
    username: string;
    maskedEmail: string;
  }>();
  @Output() showAccountLock = new EventEmitter<{
    identifier: string;
    isEmail: boolean;
  }>();
  @Input() resendConfirmationCode: boolean = false;
  @Input() resetPasswordUsername: string = '';
  forgotPasswordForm: FormGroup;

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private toast: NgToastTranslocoService,
    private loginService: LoginService,
    private loginDetailsService: LoginDetailsService,
    private contentService: ContentsService
  ) {
    this.forgotPasswordForm = this.formBuilder.group({
      email: [
        '',
        [
          Validators.required,
          ForgotPasswordComponent.accountNumberOrEmailValidator(),
        ],
      ],
    });
  }

  static accountNumberOrEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      // Check if it's a valid account number (only digits, minimum 3 digits)
      const accountNumberPattern = /^\d{3,}$/;
      const isAccountNumber = accountNumberPattern.test(value);

      // Check if it's a valid email
      const isEmail = emailPattern.test(value);

      // Must be either a valid account number OR a valid email
      if (!isAccountNumber && !isEmail) {
        return { invalidFormat: true };
      }

      return null;
    };
  }

  ngOnChanges(valueChanges: SimpleChanges) {
    if (valueChanges.resendConfirmationCode.currentValue) {
      this.resetPassword(
        valueChanges.resetPasswordUsername.currentValue,
        valueChanges.resendConfirmationCode.currentValue
      );
    }
  }

  async resetPassword(username?: string, resendConfirmationCode?: boolean) {
    let resetPasswordUsername = username
      ? username
      : this.forgotPasswordForm.get('email')?.value;

    if (
      resendConfirmationCode ||
      (resetPasswordUsername && !this.forgotPasswordForm.get('email').errors)
    ) {
      try {
        const { nextStep } = await this.authService.resetPlayerPassword(
          resetPasswordUsername
        );

        switch (nextStep.resetPasswordStep) {
          case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
            const codeDeliveryDetails = nextStep.codeDeliveryDetails;
            this.toast.toastSuccess(
              `Confirmation code was sent to ${codeDeliveryDetails.destination}`
            );
            this.resetPasswordEvent.emit({
              username: resetPasswordUsername,
              maskedEmail: codeDeliveryDetails.destination,
            });
            break;
        }
      } catch (error) {
        const errorMessage = error.message;

        // Check if error is "Missing validation data"
        if (errorMessage.includes('UserMigrationMissingValidationData')) {
          this.showVerifyDetails.emit(resetPasswordUsername);
        } else if (
          errorMessage.includes(
            'UserMigrationUserEmailAlreadyPresentNotVerifiedError'
          )
        ) {
          await this.contentService.getContentsAndConfigurationsData();
          // Check if AWS credentials are available before calling getCognitoUserInfo
          const awsCredentials =
            await this.loginDetailsService.getAwsSignatureAuthorizationConfig();
          let decryptEmail = null;
          let usernameToUse = resetPasswordUsername;
          if (awsCredentials && awsCredentials.AccessKey) {
            try {
              const cognitoUserInfo: any =
                await this.loginService.getCognitoUserInfo(
                  resetPasswordUsername
                );
              if (cognitoUserInfo && cognitoUserInfo.Username) {
                // Decrypt the Username using the encryption key
                const decryptedUsername: any = this.authService.decryptAes256Gcm(
                  cognitoUserInfo.Username,
                  encryptionKey
                );
                decryptEmail = this.authService.decryptAes256Gcm(
                  cognitoUserInfo.Email,
                  encryptionKey
                );
                usernameToUse = decryptedUsername;
                localStorage.setItem('guid_email', usernameToUse);
              }
            } catch (error) {
              // this.toast.toastWarning('getCognitoUserInfo failed:' + error);
            }
          }

          // Show OTP verification screen
          this.showOtpVerification.emit({
            username: usernameToUse,
            maskedEmail: decryptEmail || resetPasswordUsername,
          });
          return;
        }
        if (errorMessage.includes('User is disabled.')) {
          const identifier = resetPasswordUsername;
          const isEmail = identifier.includes('@');
          this.showAccountLock.emit({ identifier, isEmail });
          return;
        }
        if (
          error.message.includes(
            'Attempt limit exceeded, please try after some time'
          )
        ) {
          this.toast.toastErrorMsg(error.message.split(':')[1]);
        }
        if (error.message.includes('User not found')) {
          this.toast.toastErrorMsg('User not found.');
        }
      }
    } else {
      this.forgotPasswordForm.markAllAsTouched();
    }
  }

  goToSignIn() {
    this.goToSignin.emit();
  }
}
